import {
  Box,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useCallback, useState } from 'react';
import { useAuth, useCities, useDialog, useGuest, useSnack } from '@/logic/state';
import { useGetNearestCity, useUpdateAuthUserCity } from '@/logic/services';

import { FormattedMessage } from 'react-intl';
import { LoadingButton } from '@mui/lab';
import LocationCard from './location-card';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';

const CityLocationImg = '/icons/dialog/city-location.svg';
const MyLocationImg = '/icons/dialog/my-location.svg';

const enableLocationSelection = false;

export const CitiesForm: FC = () => {
  const theme = useTheme();
  const [selectedCityId, setSelectedCityId] = useState<string | null>(null);

  const setSnack = useSnack(s => s.setSnack, shallow);
  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);
  const cities = useCities(s => s.cities, shallow);
  const setGuest = useGuest(s => s.setGuest, shallow);
  const setUser = useAuth(s => s.setUser, shallow);

  const getNearestCity = useGetNearestCity();
  const updateAuthUserCity = useUpdateAuthUserCity();

  const loading = getNearestCity.isLoading || updateAuthUserCity.isLoading;

  const submitSelectedCity = useCallback(
    (selectedCityId: number) => {
      updateAuthUserCity.safeMutateAsync(
        {
          data: {
            cityId: selectedCityId,
          },
        },
        {
          onSuccess: data => {
            const { language: _, ...restUserData } = data.user;
            setUser(prevState => ({
              ...prevState,
              ...restUserData,
              preferredCityId: data.user.preferredCityId,
            }));
            setSnack({
              isOpen: true,
              message: 'cities_form.location.success',
              type: 'success',
            });
            setDialog({});
          },
        }
      );
    },
    [setDialog, setSnack, setUser, updateAuthUserCity]
  );

  const handleSelectedCity = useCallback(
    (selectedCityId: number) => {
      if (dialog?.citiesFormPayload?.isUser) {
        submitSelectedCity(selectedCityId);
      } else {
        setGuest(prevState => ({
          ...prevState,
          preferredCityId: selectedCityId,
        }));
        setSnack({
          isOpen: true,
          message: 'cities_form.location.success',
          type: 'success',
        });
        setDialog({});
      }
    },
    [dialog?.citiesFormPayload?.isUser, setDialog, setGuest, setSnack, submitSelectedCity]
  );

  const onSubmit = useCallback(() => {
    if (selectedCityId === 'current-location') {
      navigator.geolocation.getCurrentPosition(
        clientCurrentLocation => {
          const { coords } = clientCurrentLocation;
          getNearestCity.safeMutateAsync(
            {
              params: {
                lat: coords?.latitude,
                long: coords?.longitude,
              },
            },
            {
              onSuccess: data => handleSelectedCity(data.cityId),
            }
          );
        },
        e => {
          setSnack({
            isOpen: true,
            message:
              e.code === 1
                ? 'cities_form.location.error.permission'
                : e.code === 2
                ? 'cities_form.location.error.unavailable'
                : e.message,
            type: 'error',
          });
        }
      );
    } else {
      handleSelectedCity(Number(selectedCityId));
    }
  }, [getNearestCity, handleSelectedCity, selectedCityId, setSnack]);

  return (
    <>
      <DialogContent>
        <Typography
          id='cities-title'
          component='div'
          sx={{
            marginTop: pxToRem(52),
            fontSize: pxToRem(20),
            fontFamily: 'co-head-line-corp-bold',
            color: theme.palette.common.black,
            textAlign: 'center',
          }}
        >
          <FormattedMessage id='cities_form.title' />
        </Typography>
        <RadioGroup
          value={selectedCityId}
          onChange={e => setSelectedCityId(e.target.value)}
          sx={{
            marginTop: pxToRem(52),
            marginBottom: pxToRem(20),
          }}
        >
          {enableLocationSelection && (
            <>
              <FormControlLabel
                value='current-location'
                control={<Radio />}
                label={
                  <LocationCard
                    title={<FormattedMessage id={'location_card.title'} />}
                    subtitle='location_card.subtitle'
                    img={MyLocationImg}
                  />
                }
                sx={{
                  margin: '0px',
                  marginBottom: pxToRem(16),
                  '& .MuiRadio-root': {
                    padding: '0px',
                    marginRight: pxToRem(16),
                  },
                }}
              />

              <Divider
                sx={{
                  marginBottom: pxToRem(16),
                  border: 'none',
                  borderTop: `${pxToRem(1)} solid ${theme.palette.grey[20]}`,
                }}
              />
            </>
          )}

          <Box
            sx={{
              maxHeight: {
                mobile: `calc(100vh - ${
                  enableLocationSelection ? '328.375px' : '257.875px'
                } )`,
                tablet: enableLocationSelection ? '176px' : '246.5px',
              },
              overflow: 'auto',
            }}
          >
            {cities.citiesList.map(({ id, name, cinemaCount }) => (
              <FormControlLabel
                id={`city-${name}`}
                key={id}
                value={id}
                control={<Radio id={`city-${name}`} />}
                label={
                  <LocationCard
                    title={name}
                    subtitle={
                      cinemaCount > 1 ? 'location_card.cinemas' : 'location_card.cinema'
                    }
                    cinemasCount={cinemaCount}
                    img={CityLocationImg}
                  />
                }
                sx={{
                  display: 'flex',
                  margin: '0px',
                  marginBottom: pxToRem(16),
                  '& .MuiRadio-root': {
                    padding: '0px',
                    marginRight: pxToRem(16),
                  },
                  '&:last-of-type': {
                    marginBottom: '0px',
                  },
                }}
              />
            ))}
          </Box>
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          id='city-submit'
          variant='contained'
          fullWidth
          disabled={!selectedCityId}
          loading={loading}
          onClick={onSubmit}
          sx={{ height: { tablet: pxToRem(56), mobile: '56px' } }}
        >
          <FormattedMessage id='global.select' />
        </LoadingButton>
      </DialogActions>
    </>
  );
};
